import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen, Check } from 'lucide-react';
const tomos = [{
  id: 1,
  title: 'Herramientas para Empezar la Carrera',
  subtitle: 'Organización esencial',
  description: 'Todo lo que necesitás antes de que empiece el primer cuatrimestre. Métodos de estudio para materias teóricas y prácticas, cómo leer bibliografía académica, cómo encarar parciales y orales sin pánico y los errores más frecuentes del ingreso y cómo evitarlos desde el día uno.',
  bullets: ['Métodos de estudio específicos para EF', 'Cómo encarar orales con estructura y confianza', 'Organización de cursada semana a semana']
}, {
  id: 2,
  title: 'Ciencias Biológicas Aplicadas a la EF',
  subtitle: 'Anatomía, Fisiología y Biomecánica resueltas',
  description: 'Resúmenes estructurados y guías visuales para entender el cuerpo humano en movimiento. Dejá de acumular apuntes inentendibles y prepará tus parciales con material directo al grano.',
  bullets: ['Sistemas energéticos explicados fácil', 'Planos, ejes y movimientos articulares', 'Fichas de músculos principales y su acción']
}, {
  id: 3,
  title: 'Didáctica y Planificación en EF',
  subtitle: 'Cómo diseñar clases que funcionen',
  description: 'El paso a paso para armar planes de clase, unidades didácticas y proyectos. Modelos de enseñanza y cómo aplicarlos en el patio sin perder el control del grupo.',
  bullets: ['Estructura de un plan de clase infalible', 'Modelos de enseñanza (Mosston, TGfU)', 'Redacción de propósitos y objetivos']
}, {
  id: 4,
  title: 'Prácticas Docentes y Observación',
  subtitle: 'Todo para tu residencia en la escuela',
  description: 'Herramientas para sobrevivir y destacar en tus prácticas. Desde cómo observar una clase hasta cómo manejar imprevistos, conflictos y la relación con el docente co-formador.',
  bullets: ['Guías de observación institucional y áulica', 'Estrategias de manejo de grupo', 'Registro anecdótico y diario de campo']
}, {
  id: 5,
  title: 'Evaluación y Construcción de Instrumentos',
  subtitle: 'Rúbricas, listas de cotejo y criterios observables',
  description: 'Cómo evaluar en Educación Física de forma justa y fundamentada. Plantillas listas para usar que te darán seguridad al momento de poner una nota.',
  bullets: ['Diseño de rúbricas por nivel', 'Listas de cotejo para habilidades motoras', 'Evaluación formativa vs sumativa en el patio']
}, {
  id: 6,
  title: 'Historia, Corrientes y Marco Legal de la EF',
  subtitle: 'Las materias teóricas resueltas',
  description: 'Líneas de tiempo, resúmenes de leyes educativas y las corrientes que formaron la EF actual. Ideal para aprobar esas materias pesadas de lectura.',
  bullets: ['Línea de tiempo de la EF en Argentina', 'Resumen Ley de Educación Nacional', 'Corrientes pedagógicas comparadas']
}];
const universalChecklist = ['Contenido listo para estudiar desde el primer día', 'Conceptos clave con definición, ejemplo y aplicación práctica', 'Recursos listos para usar en parciales y prácticas docentes', 'Variantes para primer año, años intermedios y prácticas docentes', 'Checklist de contenidos por materia', 'Plantillas de planificación y evaluación', 'Adaptaciones para distintos contextos institucionales', '100% adaptado a la realidad del estudiante de EF argentino'];
export function TomosSection() {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  useEffect(() => {
    // Expand first on desktop only
    if (window.innerWidth >= 768) {
      setExpandedId(1);
    }
  }, []);
  return <section className="py-10 md:py-16 bg-gray-50" data-id="element-364">
      <div className="max-w-4xl mx-auto px-4" data-id="element-365">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-8 md:mb-12" data-id="element-366">
          <h2 className="text-2xl md:text-4xl font-black text-brand-950 mb-2 md:mb-4" data-id="element-367">
            Los 6 Tomos
          </h2>
          <p className="text-sm md:text-xl text-gray-600 font-medium" data-id="element-368">
            Para estudiantes del Profesorado de EF de todos los años
          </p>
          <p className="text-xs md:text-sm text-brand-600 font-bold mt-3 md:mt-4 bg-brand-50 inline-block px-3 py-1.5 md:px-4 md:py-2 rounded-full" data-id="element-369">
            Tocá cada tomo para ver lo que incluye 👆
          </p>
        </motion.div>

        <div className="space-y-3 md:space-y-4 mb-10 md:mb-16" data-id="element-370">
          {tomos.map(tomo => {
          const isExpanded = expandedId === tomo.id;
          return <div key={tomo.id} className={`bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden cursor-pointer ${isExpanded ? 'border-brand-500 shadow-lg' : 'border-gray-200 hover:border-brand-300 hover:shadow-md'}`} onClick={() => setExpandedId(isExpanded ? null : tomo.id)} data-id="element-371">
                <div className="p-3 md:p-5 flex items-center justify-between bg-white" data-id="element-372">
                  <div className="flex items-center gap-3 md:gap-5" data-id="element-373">
                    <div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl flex flex-col items-center justify-center flex-shrink-0 font-black text-lg md:text-xl transition-colors ${isExpanded ? 'bg-brand-600 text-white' : 'bg-brand-100 text-brand-800'}`} data-id="element-374">
                      <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-wider mb-0.5 opacity-80" data-id="element-375">
                        Tomo
                      </span>
                      {tomo.id}
                    </div>
                    <div data-id="element-376">
                      <h3 className={`text-sm md:text-lg font-bold leading-tight transition-colors ${isExpanded ? 'text-brand-900' : 'text-gray-800'}`} data-id="element-377">
                        {tomo.title}
                      </h3>
                      <p className="text-[11px] md:text-sm text-gray-500 font-medium mt-0.5 md:mt-1" data-id="element-378">
                        {tomo.subtitle}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180 text-brand-600' : ''}`} data-id="element-379" />
                </div>

                <AnimatePresence data-id="element-380">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-381">
                      <div className="px-4 md:px-5 pb-4 md:pb-5 pt-2 border-t border-gray-100 bg-gray-50/50" data-id="element-382">
                        <p className="text-gray-700 mb-4 text-xs md:text-sm leading-relaxed" data-id="element-383">
                          {tomo.description}
                        </p>
                        <ul className="space-y-2 md:space-y-3" data-id="element-384">
                          {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2 md:gap-3" data-id="element-385">
                              <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-386" />
                              <span className="text-gray-800 font-medium text-xs md:text-sm" data-id="element-387">
                                {bullet}
                              </span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-brand-900 rounded-3xl p-5 md:p-8 text-white shadow-xl relative overflow-hidden" data-id="element-388">
          <BookOpen className="absolute -right-10 -bottom-10 w-48 h-48 md:w-64 md:h-64 text-brand-800 opacity-50" data-id="element-389" />
          <div className="relative z-10" data-id="element-390">
            <h3 className="text-xl md:text-2xl font-black mb-4 md:mb-6 flex items-center gap-2 md:gap-3" data-id="element-391">
              Cada tomo incluye:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 md:gap-y-3" data-id="element-392">
              {universalChecklist.map((item, i) => <div key={i} className="flex items-start gap-2 md:gap-3" data-id="element-393">
                  <div className="mt-0.5 md:mt-1 bg-brand-700 rounded-full p-0.5 md:p-1 flex-shrink-0" data-id="element-394">
                    <Check className="w-3 h-3 md:w-4 md:h-4 text-brand-200" data-id="element-395" />
                  </div>
                  <span className="text-brand-100 font-medium text-[11px] md:text-sm leading-snug" data-id="element-396">
                    {item}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}